'use client';

import { useState, useRef, useEffect } from 'react';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useStableReferral } from '@/hooks/useStableReferral';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
interface LaunchListAPIResponse {
  success?: boolean;
  ref?: string | null;
  url?: string;
  error?: string;
  [k: string]: unknown;
}
function isLaunchListAPIResponse(v: unknown): v is LaunchListAPIResponse {
  return isRecord(v);
}

type Props = {
  keyId: string;
  className?: string;
  onSuccess?: () => void;
  onSuccessRef?: (ref: string | null, url: string) => void;
  onCaptureSubmission?: (email: string, name?: string) => void;
};

export function LaunchListDIYForm({
  keyId,
  className,
  onSuccess,
  onSuccessRef,
  onCaptureSubmission
}: Props) {
  const { isDarkMode } = useDarkMode();
  const refParam = useStableReferral();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const formRef = useRef<HTMLFormElement | null>(null);

  const action = `/api/launchlist/submit?keyId=${encodeURIComponent(keyId)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    setSubmitting(true);
    setError(null);

    try {
      const fd = new FormData();
      const emailInput = formRef.current.querySelector<HTMLInputElement>('input[name="email"]');
      const emailVal = (emailInput?.value ?? '').trim();
      if (!emailVal) {
        setError('Email required.');
        setSubmitting(false);
        return;
      }
      fd.set('email', emailVal);

      const trimmedName = fullName.trim();
      if (trimmedName) fd.set('name', trimmedName);
      if (refParam) fd.set('ref', refParam);
      onCaptureSubmission?.(emailVal, trimmedName || undefined);

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[LaunchListDIYForm][submit] outbound form', { email: emailVal, name: trimmedName || null, ref: refParam, action });
        for (const [k, v] of fd.entries()) {
          // eslint-disable-next-line no-console
            console.log('  form field ->', k, v);
        }
      }

      const res = await fetch(action, { method: 'POST', body: fd });
      let parsed: unknown = null;
      try {
        if ((res.headers.get('content-type') ?? '').includes('application/json')) {
          parsed = await res.json();
        }
      } catch { /* ignore */ }

      if (!res.ok) {
        const data = isLaunchListAPIResponse(parsed) ? parsed : {};
        const errMsg =
          (typeof data.error === 'string' ? data.error : undefined)
          ?? (res.status === 429 ? 'Rate limited. Wait a minute.'
          : res.status === 422 ? 'Invalid input.'
          : res.status === 401 || res.status === 403 ? 'Authorization failed.'
          : 'Request failed.');
        setError(errMsg);
        return;
      }

      const data = isLaunchListAPIResponse(parsed) ? parsed : {};
      onSuccess?.();
      onSuccessRef?.(data.ref ?? null, data.url ?? '');
    } catch {
      setError('Network issue. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[LaunchListDIYForm] mounted with refParam', refParam);
    }
  }, [refParam]);

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[LaunchListDIYForm] render with refParam', refParam);
  }

  return (
    <div className={className}>
      <form
        ref={formRef}
        className="launchlist-form"
        action={action}
        method="POST"
        onSubmit={handleSubmit}
      >
        <div className="grid gap-3 sm:gap-4">
          {refParam && <input type="hidden" name="ref" value={refParam} />}

          <div className="space-y-1">
            <label
              htmlFor="fullName"
              className={`block text-xs font-medium tracking-wide ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Name <span className="opacity-60 lowercase">(optional)</span>
            </label>
            <input
              id="fullName"
              name="name"
              type="text"
              autoComplete="name"
              placeholder="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={`w-full rounded-md px-3 py-2 border outline-none transition ${
                isDarkMode
                  ? 'bg-gray-800/70 border-gray-700 text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-indigo-500'
              }`}
            />
          </div>

          <div className="grid gap-1.5">
            <label
              htmlFor="ll-email"
              className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
            >
              Email
            </label>
            <input
              id="ll-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
              className={`w-full rounded-md px-3 py-2 border outline-none transition ${
                isDarkMode
                  ? 'bg-gray-800/70 border-gray-700 text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-indigo-500'
              }`}
              disabled={submitting}
            />
          </div>

          {error && <div className="text-red-500 text-sm">{error}</div>}

          <div className="flex w-full items-center justify-end gap-3 pt-1">
            <button
              type="submit"
              disabled={submitting}
              className={`inline-flex items-center rounded-md px-4 py-2 font-semibold transition shadow ${
                submitting ? 'opacity-70 cursor-not-allowed' : 'hover:brightness-110 hover:shadow-xl'
              } ${
                isDarkMode
                  ? 'bg-venetian-night text-white border border-gray-700'
                  : 'bg-meteor-shower text-white border border-transparent'
              }`}
            >
              {submitting ? 'Signing up…' : 'Sign Up'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}